import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerJobCardComponent } from '../../components/runner-job-card/runner-job-card.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { ResultStatus, RunnerCase, RunnerState, RunnerStep } from '../../models/test-run.model';
import { TestRunService } from '../../services/test-run.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import { formatDurationMs } from '../../shared/duration.util';
import { TEST_RUN_STATUS_ICON } from '../../shared/test-run-outcome';
import { findRouteParam } from '../../shared/route-params';
import { statusKey } from '../test-results/test-results.types';
import { resolveIterationValues } from '../testlauf-runner/iteration-values';
import { decidedSteps, leafSteps } from '../testlauf-runner/runner-steps';
import { NumberedStep, numberSteps } from '../testlauf-runner/step-numbering';

interface CaseSection {
  runnerCase: RunnerCase;
  entries: NumberedStep[];
}

/**
 * Die Ergebnisse eines abgeschlossenen Durchlaufs, nur zum Lesen. Aufbau und Klassen
 * folgen der Testergebnis-Detailseite; die Zahlen sind dieselben wie auf der
 * Abschluss-Karte des Runners, damit beide Ansichten nie Verschiedenes melden.
 */
@Component({
  selector: 'app-testlauf-ergebnisse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatIcon, MatProgressSpinner, TranslatePipe, ProjectNavComponent, RunnerJobCardComponent],
  templateUrl: './testlauf-ergebnisse.component.html',
  styleUrls: ['../test-result-detail/test-result-detail.component.scss', './testlauf-ergebnisse.component.scss'],
})
export class TestlaufErgebnisseComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly testRunService = inject(TestRunService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly statusKey = statusKey;

  protected readonly projectId = this.findParam('projectId');
  private readonly runId = this.findParam('runId');
  private readonly executionId = this.findParam('executionId');

  protected readonly executionsLink = ['/projects', this.projectId, 'test-runs', this.runId, 'executions'];
  protected readonly testResultLink = ['/projects', this.projectId, 'test-results', this.runId];

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly state = signal<RunnerState | null>(null);
  /** Das Testergebnis-Detail zeigt nur den neuesten Durchlauf; nur dort fuehrt der Link zu den eigenen Screenshots. */
  protected readonly testResultLinkShown = signal(false);

  protected readonly automated = computed(() => this.state()?.runKind === 'AUTOMATED');
  protected readonly environment = computed(() => this.state()?.environment?.trim() || '—');
  protected readonly outcome = computed<ResultStatus>(() => this.state()?.status ?? 'NOT_RUN');
  protected readonly outcomeIcon = computed(() => TEST_RUN_STATUS_ICON[this.outcome()]);
  protected readonly duration = computed(() => formatDurationMs(this.state()?.durationMs));

  private readonly leaves = computed(() => leafSteps(this.state()?.cases ?? []));
  protected readonly failed = computed(() => this.countLeaves('FAILED'));
  protected readonly blocked = computed(() => this.countLeaves('BLOCKED'));
  protected readonly skipped = computed(() => this.countLeaves('SKIPPED'));
  protected readonly hasStepResults = computed(() => decidedSteps(this.state()?.cases ?? []).length > 0);

  protected readonly sections = computed<CaseSection[]>(() =>
    [...(this.state()?.cases ?? [])]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((runnerCase) => ({ runnerCase, entries: numberSteps(resolveIterationValues(runnerCase.steps)) })),
  );

  constructor() {
    this.runnerService
      .getState(this.projectId, this.runId, this.executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runnerState) => {
          if (runnerState.live) {
            this.openRunner();
          } else if (runnerState.runKind === 'AUTOMATED') {
            this.showRunnerRunOrOpenRunner(runnerState);
          } else {
            this.show(runnerState);
          }
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  /**
   * Automatisierte Durchlaeufe gehoeren in den Runner, ausser sie stammen von einem
   * verbundenen Rechner (TCDL): deren Schritte gibt es nur hier. Pipeline-Laeufe bleiben
   * in der Live-Ansicht, dort haengt die CI-Zuordnung. Ohne Quelle gilt das bisherige Ziel.
   */
  private showRunnerRunOrOpenRunner(runnerState: RunnerState): void {
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          if (run.source !== 'TCDL') {
            this.openRunner();
            return;
          }
          this.show(runnerState);
          this.showTestResultLinkIfNewest();
        },
        error: () => this.openRunner(),
      });
  }

  /** Scheitert das Laden, fehlt nur der Link. */
  private showTestResultLinkIfNewest(): void {
    this.testRunService
      .listExecutions(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (executions) => {
          const newestNr = Math.max(...executions.map((execution) => execution.nr));
          this.testResultLinkShown.set(executions.some((e) => e.id === this.executionId && e.nr === newestNr));
        },
        error: () => {
          // Bewusst still: die Ergebnisse bleiben stehen.
        },
      });
  }

  private show(runnerState: RunnerState): void {
    this.state.set(runnerState);
    this.loading.set(false);
  }

  /** Ersetzt die Ergebnis-URL, damit "Zurueck" im Browser nicht hier landet und erneut umleitet. */
  private openRunner(): void {
    void this.router.navigate(['/projects', this.projectId, 'test-runs', this.runId, 'executions', this.executionId], {
      replaceUrl: true,
    });
  }

  protected stepDuration(step: RunnerStep): string {
    return formatDurationMs(step.durationMs);
  }

  private countLeaves(status: ResultStatus): number {
    return this.leaves().filter((step) => step.actualStatus === status).length;
  }

  private findParam(name: string): string {
    return findRouteParam(this.route, name) ?? '';
  }
}
